import { createFileRoute, useParams, Link } from '@tanstack/react-router'
import { Loader2, Search, FileSearch, Youtube, Sparkles, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useProjects } from '@/hooks/useProjects'
import { useProjectContext } from '@/hooks/useBootstrapContext'
import { PageHeader } from '@/components/shared/PageHeader'

export const Route = createFileRoute('/_authenticated/projekty/$projectId/')({
  component: ProjectDetailPage,
})

const statusLabels: Record<string, string> = {
  active: 'Aktywny',
  paused: 'Wstrzymany',
  archived: 'Zarchiwizowany',
}

const statusVariants: Record<string, 'default' | 'secondary' | 'outline'> = {
  active: 'default',
  paused: 'secondary',
  archived: 'outline',
}

function ProjectDetailPage() {
  const { projectId } = useParams({
    from: '/_authenticated/projekty/$projectId/',
  })
  const { data: projects, isLoading } = useProjects()
  const project = projects?.find((p) => p.id === projectId)
  const { data: ctx } = useProjectContext(projectId)
  const isBootstrapped = !!ctx?.isBootstrapped

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--text-muted)' }} />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="space-y-4">
        <p style={{ color: 'var(--text-muted)' }}>Nie znaleziono projektu.</p>
        <Button variant="outline" asChild>
          <Link to="/projekty">Wróć do projektów</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Przegląd"
        breadcrumbItems={[
          { label: 'Projekty', to: '/projekty' },
          { label: project.name },
        ]}
        badge={
          <Badge variant={statusVariants[project.status] ?? 'outline'}>
            {statusLabels[project.status] ?? project.status}
          </Badge>
        }
        subtitle={
          <a
            href={project.url.startsWith('http') ? project.url : `https://${project.url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline underline-offset-2"
            style={{ color: 'var(--text-muted)' }}
          >
            {project.url}
          </a>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Krok 0: Cold Start — Kontekst SEO (podstawa dla świeżych domen) */}
        <Card
          className="relative transition-shadow hover:shadow-md"
          style={{
            background: 'var(--bg-secondary)',
            borderColor: isBootstrapped ? '#10b981' : 'var(--border-color)',
          }}
        >
          {isBootstrapped && (
            <div
              className="absolute -top-2.5 right-3 rounded-full px-2.5 py-0.5 text-xs font-medium text-white flex items-center gap-1"
              style={{ backgroundColor: '#10b981' }}
            >
              <CheckCircle2 className="h-3 w-3" />
              Gotowe
            </div>
          )}
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
              <div
                className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold"
                style={{
                  backgroundColor: isBootstrapped ? '#10b981' : 'var(--bg-primary)',
                  color: isBootstrapped ? 'white' : 'var(--text-muted)',
                  border: isBootstrapped ? 'none' : '1px solid var(--border-color)',
                }}
              >
                0
              </div>
              <Sparkles className="h-5 w-5" />
              Kontekst SEO
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
              {isBootstrapped
                ? `Wygenerowany — ${ctx?.keywords.length ?? 0} fraz, ${ctx?.competitors.length ?? 0} konkurentów.`
                : 'AI wykryje branżę, ICP i wygeneruje 10-20 fraz target dla świeżej domeny.'}
            </p>
            <Button size="sm" variant={isBootstrapped ? 'outline' : 'default'} asChild>
              <Link to="/projekty/$projectId/cold-start" params={{ projectId: project.id }}>
                {isBootstrapped ? 'Zobacz kontekst' : 'Wygeneruj kontekst'}
              </Link>
            </Button>
          </CardContent>
        </Card>

        {/* Krok 1: Audyt On-Page — PRIMARY */}
        <Card
          className="relative transition-shadow hover:shadow-md border-2"
          style={{ background: 'var(--bg-secondary)', borderColor: '#3b82f6' }}
        >
          <div
            className="absolute -top-2.5 right-3 rounded-full px-2.5 py-0.5 text-xs font-medium text-white"
            style={{ backgroundColor: '#3b82f6' }}
          >
            Zacznij tutaj
          </div>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
              <div
                className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white"
                style={{ backgroundColor: '#3b82f6' }}
              >
                1
              </div>
              <FileSearch className="h-5 w-5" />
              Audyt On-Page
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
              Pełny audyt SEO on-page z rekomendacjami optymalizacji.
            </p>
            <Button size="sm" asChild>
              <Link to="/projekty/$projectId/audyt" params={{ projectId: project.id }}>
                Uruchom audyt
              </Link>
            </Button>
          </CardContent>
        </Card>

        {/* Krok 2: Analiza SERP — SECONDARY */}
        <Card
          className="transition-shadow hover:shadow-md"
          style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
              <div
                className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold"
                style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}
              >
                2
              </div>
              <Search className="h-5 w-5" />
              Analiza SERP
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
              Śledź pozycje w wyszukiwarce, analizuj konkurentów i znajdź luki w treściach.
            </p>
            <Button size="sm" variant="outline" asChild>
              <Link to="/projekty/$projectId/serp" params={{ projectId: project.id }}>
                Otwórz analizę SERP
              </Link>
            </Button>
          </CardContent>
        </Card>

        {/* Krok 3: YouTube SEO — TERTIARY */}
        <Card
          className="transition-shadow hover:shadow-md"
          style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
              <div
                className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold"
                style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}
              >
                3
              </div>
              <Youtube className="h-5 w-5" />
              YouTube SEO
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
              Optymalizacja tytułów, opisów i tagów filmów YouTube.
            </p>
            <Button size="sm" variant="outline" asChild>
              <Link to="/projekty/$projectId/youtube-seo" params={{ projectId: project.id }}>
                Otwórz YouTube SEO
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
